import {useCallback, useReducer, useEffect, useRef, useState} from 'react';
import {
  getSettingsFromDigestType,
  getNextDigestUTCDateTime,
  defaultDigestDay,
  defaultDigestFrequency,
  defaultDigestHour,
  defaultDigestSendOnceHours
} from '../../modules/digest_utils';
import DigestAudienceSettings from './_digest_audience_settings';
import DigestFrequencySettings from './_digest_frequency_settings';
import DigestArticleSettings from './_digest_articles_settings';
import DigestName from './_digest_name';
import DigestSettingError from './_digest_setting_error';
import classNames from 'classnames';

const reducer = (state, {payload: {
  digestType
}, type}) => {
  switch(type) {
    case 'INITIALIZE':
      return getSettingsFromDigestType(digestType);
    default: return state;
  }
};

const calcReviewSendOnceAt = reviewSendOnceAt => {
  return reviewSendOnceAt ? Math.ceil(moment.duration(moment(reviewSendOnceAt).diff(moment())).asHours()) : defaultDigestSendOnceHours;
};

const DigestTypeSettings = ({
  digest,
  activeDigestType,
  activeDigestTypeIsNew,
  digestTypes,
  visibilityGroups,
  multipleDigestsEnabled,
  showDoneButton,
  showNameSection,
  onClose,
  onCancel,
  digestGet,
  digestTypeUpdate,
  confirm
}) => {
  const [{
    name,
    digestTypeId,
    reviewFrequency,
    reviewDay,
    reviewHour,
    reviewRole,
    reviewSendOnceAt,
    reviewItemsPerRival
  }, dispatch] = useReducer(reducer, getSettingsFromDigestType(activeDigestType));
  const isMounted = useRef(false);
  const getDigestSequenceNumber = useRef(0);
  const [digestType, setDigestType] = useState(activeDigestType);
  const [dirtyName, setDirtyName] = useState();
  const [saved, setSaved] = useState(false);
  const [nameError, setNameError] = useState();
  const [sendTime, setSendTime] = useState(getNextDigestUTCDateTime(digest || {}));
  const [sendOnceHours, setSendOnceHours] = useState(calcReviewSendOnceAt(reviewSendOnceAt));

  const handleUpdateError = (xhr, type) => {
    setNameError(<DigestSettingError message={xhr?.responseJSON?.message || 'An unknown error occurred while updating the digest type.'} />);
  };

  const refreshDigestSendTime = useCallback(callback => {
    const sn = ++getDigestSequenceNumber.current;

    digestGet(0, digestTypeId, updatedDigest => {
      if(!isMounted.current || (sn !== getDigestSequenceNumber.current)) {
        return;
      }

      setSendTime(getNextDigestUTCDateTime(updatedDigest));
      typeof callback === 'function' && callback();
    });
  }, [digestGet, digestTypeId]);

  const updateDigestType = useCallback((options, refreshSendTime = false, callback) => {
    digestTypeUpdate(options, (updated, xhr, type) => {
      if(!isMounted.current) {
        return;
      }

      if(!updated) {
        return handleUpdateError(xhr, type);
      }

      setSaved(true);
      setDigestType(updated);

      if(refreshSendTime) {
        return refreshDigestSendTime(callback);
      }

      typeof callback === 'function' && callback(updated);
    });
  }, [digestTypeUpdate, refreshDigestSendTime]);

  const handleCancel = useCallback(() => {
    onCancel(digestType);
  }, [onCancel, digestType]);

  const handleClose = useCallback(() => {
    if(nameError) {
      return;
    }

    if(typeof dirtyName !== 'string' || dirtyName === digestType?.name) {
      return onClose((saved || activeDigestTypeIsNew) ? digestType : null);
    }

    updateDigestType({
      id: digestTypeId,
      name: dirtyName
    }, false, updated => {
      if(updated) {
        onClose(updated);
      }
      else {
        setNameError(<DigestSettingError message="There was an error updating the name. Please try again." />);
      }
    });
  }, [dirtyName, digestType, digestTypeId, onClose, updateDigestType, nameError, saved]);

  useEffect(() => {
    const keyCheck = event => {
      if(event?.key === 'Escape') {
        return handleClose();
      }
    };

    window.addEventListener('keydown', keyCheck);

    return () => window.removeEventListener('keydown', keyCheck);
  }, [handleClose]);
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    dispatch({type: 'INITIALIZE', payload: {digestType}});
  }, [digestType]);

  useEffect(() => {
    setSendTime(getNextDigestUTCDateTime(digest));
  }, [digest]);

  useEffect(() => {
    setSendOnceHours(calcReviewSendOnceAt(reviewSendOnceAt));
  }, [reviewSendOnceAt]);

  const handleVisibilityGroupSelected = (ids, selected) => {
    const newReviewRole = [...reviewRole];

    if(selected) {
      newReviewRole.push(...ids);
    }
    else {
      ids.forEach(id => {
        const index = newReviewRole.indexOf(id);

        newReviewRole.splice(index, 1);
      });
    }

    updateDigestType({
      id: digestTypeId,
      reviewRole: newReviewRole
    });
  };

  const handleToggleReviewRole = useCallback(role => {
    if(!role || (role === reviewRole)) {
      return;
    }

    const newReviewRole = (role === 'visibilityGroups') ? [] : (role || 'consumer');

    updateDigestType({
      id: digestTypeId,
      reviewRole: newReviewRole
    });
  }, [reviewRole, updateDigestType, digestTypeId]);

  const handleSetSendOnceHours = hours => {
    const sendOnceAt = moment().add(hours, 'h').toISOString();

    const digestTypeOptions = {
      id: digestTypeId,
      reviewSendOnceAt: sendOnceAt
    };

    updateDigestType(digestTypeOptions, true);
  };

  const handleSetReviewFrequency = (frequency, callback) => {
    const frequencyOptions = {
      id: digestTypeId,
      reviewFrequency: frequency
    };

    if(frequency === 'once') {
      if(!reviewSendOnceAt) {
        frequencyOptions.reviewSendOnceAt = moment().add(defaultDigestSendOnceHours, 'h').toISOString();
      }
    }

    updateDigestType(frequencyOptions, true, callback);
  };

  const handleSetReviewDay = day => {
    updateDigestType({
      id: digestTypeId,
      reviewDay: day
    }, true);
  };

  const handleSetReviewHour = hour => {
    updateDigestType({
      id: digestTypeId,
      reviewHour: hour
    }, true);
  };

  const handleResetToDefaults = () => {
    const resetToDefaults = () => {
      updateDigestType({
        id: digestTypeId,
        reviewFrequency: defaultDigestFrequency,
        reviewDay: defaultDigestDay,
        reviewHour: defaultDigestHour,
        reviewSendOnceAt: null
      }, true);
    };

    confirm({
      message: 'Do you want to reset this Digest Schedule to the default schedule?',
      okCallback: resetToDefaults,
      buttonOk: 'Reset to Defaults'
    });
  };

  const handleSetReviewItemsPerRival = itemsPerRival => {
    if(itemsPerRival < 0 || itemsPerRival > 10 || itemsPerRival === reviewItemsPerRival) {
      return;
    }

    updateDigestType({
      id: digestTypeId,
      reviewItemsPerRival: itemsPerRival
    });
  };

  const handleNameChange = updatedName => {
    setDirtyName(updatedName);
  };

  const nameIsUnique = nameToCheck => {
    const lowerCaseName = nameToCheck.toLowerCase();

    return !digestTypes.some(dt => (dt.id !== digestTypeId) && (dt.name.toLowerCase() === lowerCaseName));
  };

  const handleNameBlur = () => {
    // we can check the name here for uniqueness
    if(typeof dirtyName === 'string') {
      const trimmedName = dirtyName.trim();

      if(!nameIsUnique(trimmedName)) {
        setNameError(<DigestSettingError message="That name is already in use." />);
      }
      else if(!trimmedName.length) {
        setNameError(<DigestSettingError message="Names cannot be blank." />);
      }
      else if(trimmedName.length > 60) {
        setNameError(<DigestSettingError message="Maximum name length is 60 characters." />);
      }
      else {
        setNameError(null);
      }
    }
  };

  if(!digestType) {
    return null;
  }

  return (
    <div className={classNames('digest-type-settings', {multiples: multipleDigestsEnabled})}>
      <h3 className="u-m0">Manage {multipleDigestsEnabled ? 'this' : 'the'} Intel Digest</h3>
      {showNameSection && multipleDigestsEnabled && <DigestName
        name={typeof dirtyName === 'string' ? dirtyName : name}
        onNameChange={handleNameChange}
        onNameBlur={handleNameBlur}
        error={nameError} />}
      <DigestAudienceSettings
        reviewRole={reviewRole}
        multipleDigestsEnabled={multipleDigestsEnabled}
        visibilityGroups={visibilityGroups}
        onToggleReviewRole={handleToggleReviewRole}
        onVisibilityGroupSelected={handleVisibilityGroupSelected} />
      <DigestFrequencySettings
        reviewFrequency={reviewFrequency}
        reviewDay={reviewDay}
        reviewHour={reviewHour}
        sendOnceHours={sendOnceHours}
        sendTime={sendTime}
        onSetSendOnceHours={handleSetSendOnceHours}
        onSetReviewFrequency={handleSetReviewFrequency}
        onSetReviewDay={handleSetReviewDay}
        onSetReviewHour={handleSetReviewHour}
        onResetToDefaults={handleResetToDefaults} />
      <DigestArticleSettings
        reviewItemsPerRival={reviewItemsPerRival}
        onSetReviewItemsPerRival={handleSetReviewItemsPerRival} />
      {(showDoneButton || onCancel) && <div className="digest-settings-modal__footer">
        {onCancel && <button
          data-test-id="digest-find-content-button"
          data-tracking-id="digest-find-content-button"
          className="button button--alt"
          onClick={handleCancel}>
          Cancel
        </button>}
        {showDoneButton && <button
          data-test-id="digest-find-content-button"
          data-tracking-id="digest-find-content-button"
          className="button button--alt"
          onClick={handleClose}>
          Done
        </button>}
      </div>}
    </div>
  );
};

DigestTypeSettings.propTypes = {
  digest: PropTypes.object,
  activeDigestType: PropTypes.object,
  activeDigestTypeIsNew: PropTypes.bool,
  digestTypes: PropTypes.arrayOf(PropTypes.object),
  visibilityGroups: PropTypes.arrayOf(PropTypes.object),
  multipleDigestsEnabled: PropTypes.bool,
  showDoneButton: PropTypes.bool,
  showNameSection: PropTypes.bool,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  digestTypeUpdate: PropTypes.func,
  digestGet: PropTypes.func,
  confirm: PropTypes.func
};

DigestTypeSettings.defaultProps = {
  digest: null,
  activeDigestType: null,
  activeDigestTypeIsNew: false,
  digestTypes: [],
  visibilityGroups: [],
  multipleDigestsEnabled: false,
  showDoneButton: false,
  showNameSection: false,
  onClose() {},
  onCancel: null,
  digestTypeUpdate() {},
  digestGet() {},
  confirm() {}
};

export default DigestTypeSettings;
